/**
 * Utilitários para cálculo de consumo de produtos de limpeza.
 */
import {
    dayKeysInPeriod,
    extractHourFromCreateTime,
    getCleaningReportDayKey,
    getCleaningReportTaskStartTime,
    hasMeasuredConsumptionValue,
} from './cleaningReportUtils';

/**
 * Calcula o consumo total de água em litros a partir da lista de tarefas e métricas amostradas.
 *
 * Intenção: Obter o volume de água gasto (real ou projetado) para o dashboard.
 * Exemplo: const water = calculateTotalWaterSpent(list, 1000, { discharge_rate: 15 });
 *
 * @param {Array} list Lista de tarefas.
 * @param {number} totalArea Área total.
 * @param {Object} calculatedMetrics Métricas enviadas pelo backend (discharge_rate amostrada).
 * @param {number} manualDischargeRate Taxa de descarga configurada (ml/m²).
 * @returns {number} Consumo total de água em Litros.
 */
export function calculateTotalWaterSpent(
    list,
    totalArea = 0,
    calculatedMetrics = null,
    manualDischargeRate = 20
) {
    if (!list || list.length === 0) return 0;

    const areaNum = parseFloat(totalArea) || 0;

    // 1. Prioridade: Métrica calculada por amostragem inteligente no backend
    if (calculatedMetrics && calculatedMetrics.discharge_rate) {
        return (areaNum * calculatedMetrics.discharge_rate) / 1000;
    }

    // 2. Secundário: Se houverem dados reais individuais (detalhamento total)
    const hasRealData = list.some(
        item => item.cost_water !== undefined && item.cost_water !== null
    );
    if (hasRealData) {
        const totalMl = list.reduce(
            (acc, item) => acc + (parseFloat(item.cost_water) || 0),
            0
        );
        return totalMl / 1000;
    }

    // 3. Fallback: Estimativa baseada na configuração ou valor padrão
    const rate = parseFloat(manualDischargeRate) || 20;
    return (areaNum * rate) / 1000;
}

/**
 *
 * Intenção: Calcular o volume de produto puro necessário para a mistura.
 * Exemplo: const chem = calculateChemicalConsumption(10, 50); // 500ml
 *
 * @param {number} waterL Volume de água em Litros.
 * @param {number} dilutionRatio Proporção de diluição em ml/L.
 * @returns {number} Volume de produto químico em mililitros.
 */
export function calculateChemicalConsumption(waterL, dilutionRatio) {
    const ratio = parseFloat(dilutionRatio) || 10;
    return waterL * ratio;
}

/**
 *
 * Intenção: Comparar a eficiência do robô contra o método de limpeza tradicional.
 * Exemplo: const saved = calculateWaterSavings(100, 2); // Litros economizados
 *
 * @param {number} totalArea Área total limpa.
 * @param {number} waterL Água realmente gasta pelo robô.
 * @param {number} manualReference Referência manual em ml/m².
 * @returns {number} Economia em Litros.
 */
export function calculateWaterSavings(
    totalArea,
    waterL,
    manualReference = 500
) {
    const areaNum = parseFloat(totalArea) || 0;
    const manualRefL = (areaNum * manualReference) / 1000;
    const savings = manualRefL - waterL;
    return Math.max(0, savings);
}

/**
 * Formata volumes de fluidos para exibição amigável (ml vs L).
 *
 * Intenção: Converter automaticamente mililitros para litros quando o volume
 * for significativo, melhorando a leitura para o cliente.
 *
 * @param {number} ml Volume em mililitros.
 * @returns {Object} Objeto com valor formatado e unidade.
 */
export function formatFluidVolume(ml) {
    const value = parseFloat(ml) || 0;
    if (value >= 1000) {
        return {
            value: (value / 1000).toFixed(1),
            unit: 'L',
        };
    }
    return {
        value: value.toFixed(0),
        unit: 'ml',
    };
}

/**
 * Calcula a média de diluição entre os robôs da lista.
 *
 * Intenção: Obter uma taxa de diluição representativa para o dashboard quando
 * múltiplos robôs com configurações diferentes estão sendo visualizados.
 *
 * @param {Array} list Lista de tarefas.
 * @param {Object} applications Mapeamento de configurações por robô.
 * @param {number} defaultRatio Diluição padrão se não houver dados.
 * @returns {number} Média de diluição em ml/L.
 */
export function calculateAverageDilution(
    list,
    applications,
    defaultRatio = 10
) {
    if (!list || list.length === 0 || !applications) return defaultRatio;

    const robotIds = Array.from(
        new Set(list.map(t => t._robot && t._robot.id).filter(Boolean))
    );
    if (robotIds.length === 0) return defaultRatio;

    let total = 0;
    let count = 0;

    robotIds.forEach(id => {
        if (applications[id] && applications[id].chemicalDilutionRatio) {
            total += parseFloat(applications[id].chemicalDilutionRatio);
            count++;
        }
    });

    return count > 0 ? total / count : defaultRatio;
}

function snKey(sn) {
    return String(sn || '')
        .trim()
        .toLowerCase();
}

function parseDilutionMlPerL(value, fallback = 10) {
    if (value === '' || value == null) return fallback;
    const n = Number(value);
    if (!Number.isFinite(n) || n < 0) return fallback;
    return n;
}

function withSnFromTaskList(robotDilutions, list) {
    const byId = {};
    (list || []).forEach(task => {
        const id = task && task._robot && task._robot.id;
        const sn = task && (task.sn || task.device_sn);
        if (id != null && sn) {
            byId[String(id)] = String(sn);
        }
    });
    return (robotDilutions || []).map(row => {
        const fromTask =
            row && row.robot_id != null ? byId[String(row.robot_id)] : '';
        const sn = fromTask || (row && row.sn) || '';
        return { ...row, sn };
    });
}

/**
 * Diluição (ml de concentrado por litro de água) da aplicação de limpeza
 * ligada ao robô. Sem aplicação cadastrada, retorna null.
 */
export function getRobotDilutionMlPerL(
    robotDilutions,
    robot,
    list = [],
    fallbackRatio = 10
) {
    const dilutions = withSnFromTaskList(robotDilutions, list).filter(
        row => row && Number.isFinite(Number(row.dilution_ml_per_l))
    );
    if (!dilutions.length || !robot) return null;

    const id = robot.robot_id != null ? String(robot.robot_id) : '';
    if (id) {
        const byId = dilutions.find(
            row => row.robot_id != null && String(row.robot_id) === id
        );
        if (byId)
            return parseDilutionMlPerL(byId.dilution_ml_per_l, fallbackRatio);
    }

    const sn = snKey(robot.sn);
    if (sn) {
        const bySn = dilutions.find(row => snKey(row.sn) === sn);
        if (bySn)
            return parseDilutionMlPerL(bySn.dilution_ml_per_l, fallbackRatio);
    }

    return null;
}

/** Produto concentrado (ml) = água (L) × diluição (ml/L). */
export function estimateRobotProductMl(waterL, dilutionMlPerL) {
    const water = Number(waterL);
    const ratio = Number(dilutionMlPerL);
    if (!Number.isFinite(water) || water <= 0) return 0;
    if (!Number.isFinite(ratio) || ratio < 0) return 0;
    return Number((water * ratio).toFixed(1));
}

/**
 * Produto concentrado no período: água da Pudu × diluição (ml/L) da
 * aplicação de limpeza de cada robô filtrado.
 *
 * Não usa média da frota quando há água por SN. Robô sem aplicação de
 * limpeza não entra no cálculo.
 *
 * @returns {number|null} mililitros de produto, ou null se não houver base.
 */
export function estimateProductConsumptionMl({
    consumption,
    robotDilutions = [],
    list = [],
    fallbackRatio = 10,
} = {}) {
    const dilutions = withSnFromTaskList(robotDilutions, list).filter(
        row => row && Number.isFinite(Number(row.dilution_ml_per_l))
    );
    if (!dilutions.length) {
        return null;
    }

    const bySn = {};
    let ratioSum = 0;
    dilutions.forEach(row => {
        const ratio = parseDilutionMlPerL(row.dilution_ml_per_l, fallbackRatio);
        ratioSum += ratio;
        const key = snKey(row.sn);
        if (key) bySn[key] = ratio;
    });
    const avgRatio = ratioSum / dilutions.length;

    if (!consumption || consumption.source !== 'pudu_analysis') {
        let hasAnyWater = false;
        let productMl = 0;
        (list || []).forEach(task => {
            if (task && task.cost_water != null) {
                const w = Number(task.cost_water);
                if (Number.isFinite(w) && w > 0) {
                    hasAnyWater = true;
                    const sn = snKey(
                        task.sn ||
                            task.device_sn ||
                            (task._robot &&
                                (task._robot.code || task._robot.id))
                    );
                    const ratio = (sn && bySn[sn]) || avgRatio;
                    productMl += (w / 1000) * ratio;
                }
            }
        });
        return hasAnyWater ? productMl : null;
    }
    const waterLTotal = (Number(consumption.water_ml) || 0) / 1000;

    const byRobot = Array.isArray(consumption.by_robot)
        ? consumption.by_robot
        : [];
    if (byRobot.length && Object.keys(bySn).length) {
        let productMl = 0;
        let matched = false;
        byRobot.forEach(row => {
            const key = snKey(row && row.sn);
            if (!key || !Object.prototype.hasOwnProperty.call(bySn, key)) {
                return;
            }
            matched = true;
            const waterL = (Number(row.water_ml) || 0) / 1000;
            productMl += waterL * bySn[key];
        });
        if (matched) return productMl;
    }

    return waterLTotal * avgRatio;
}

/**
 * Constrói a série temporal de consumo de produto para o gráfico.
 * Suporta agrupamento por hora em períodos de 1 dia e por dia em períodos múltiplos.
 */
export function buildProductConsumptionSeries({
    list = [],
    consumption = null,
    robotDilutions = [],
    periodDates = null,
    fallbackRatio = 10,
} = {}) {
    const isSingleDay = !!(
        periodDates &&
        periodDates.startDate &&
        periodDates.endDate &&
        periodDates.startDate === periodDates.endDate
    );

    const dilutions = withSnFromTaskList(robotDilutions, list).filter(
        row => row && Number.isFinite(Number(row.dilution_ml_per_l))
    );
    const bySn = {};
    let ratioSum = 0;
    dilutions.forEach(row => {
        const ratio = parseDilutionMlPerL(row.dilution_ml_per_l, fallbackRatio);
        ratioSum += ratio;
        const key = snKey(row.sn);
        if (key) bySn[key] = ratio;
    });
    const avgRatio =
        dilutions.length > 0 ? ratioSum / dilutions.length : fallbackRatio;

    const getTaskDilution = task => {
        const sn = snKey(
            (task && task.sn) ||
                (task && task.device_sn) ||
                (task && task._robot && (task._robot.code || task._robot.id))
        );
        return (sn && bySn[sn]) || avgRatio;
    };

    if (isSingleDay) {
        const byHour = new Array(24).fill(0);
        (list || []).forEach(task => {
            if (task && hasMeasuredConsumptionValue(task.cost_water)) {
                const hour = extractHourFromCreateTime(
                    getCleaningReportTaskStartTime(task)
                );
                if (hour != null) {
                    const waterL = (Number(task.cost_water) || 0) / 1000;
                    const dilution = getTaskDilution(task);
                    byHour[hour] += waterL * dilution;
                }
            }
        });
        const totalMl = byHour.reduce((s, n) => s + n, 0);
        return {
            unit: 'hour',
            totalMl: Math.round(totalMl * 10) / 10,
            data: byHour.map((ml, hour) => ({
                date: `${String(hour).padStart(2, '0')}h`,
                rawDate: `${String(hour).padStart(2, '0')}h`,
                value: Math.round(ml * 10) / 10,
            })),
        };
    }

    const seeded = dayKeysInPeriod(periodDates);
    const byDay = {};
    seeded.forEach(k => {
        byDay[k] = 0;
    });

    (list || []).forEach(task => {
        if (task && hasMeasuredConsumptionValue(task.cost_water)) {
            const dayKey = getCleaningReportDayKey(
                getCleaningReportTaskStartTime(task)
            );
            if (dayKey && dayKey !== '—') {
                if (byDay[dayKey] === undefined) {
                    byDay[dayKey] = 0;
                }
                const waterL = (Number(task.cost_water) || 0) / 1000;
                const dilution = getTaskDilution(task);
                byDay[dayKey] += waterL * dilution;
            }
        }
    });

    const sortedDays = (seeded.length > 0 ? seeded : Object.keys(byDay)).sort();
    let totalFromDays = 0;
    const data = sortedDays.map(key => {
        const ml = byDay[key] || 0;
        totalFromDays += ml;
        const parts = key.split('-');
        const dateFmt =
            parts.length === 3 ? `${parts[2]}-${parts[1]}-${parts[0]}` : key;
        return {
            date: dateFmt,
            rawDate: key,
            value: Math.round(ml * 10) / 10,
        };
    });

    const estimatedTotal = estimateProductConsumptionMl({
        consumption,
        robotDilutions,
        list,
        fallbackRatio,
    });

    return {
        unit: 'day',
        totalMl:
            estimatedTotal != null
                ? Math.round(estimatedTotal * 10) / 10
                : Math.round(totalFromDays * 10) / 10,
        data,
    };
}
